import React, { useContext, useEffect, useState, useMemo } from "react";
import {mock, headerRouterTable} from "./utils"
import {
    Table,
    HeaderRow,
    ItemRow,
    HeaderCell,
    ItemCell,
    ItemCellContainer,
    ImageCell,
    OptionsImage,
    StatusText
}from "./style"

import details from '../../../../assets/router/details.svg';
import edit from '../../../../assets/router/edit-pen.svg'; 
import trash from '../../../../assets/router/trash.svg';
import { RouterContext, Router } from "../../../../contexts/RouterContext";
import SectionContext from "../../../../contexts/SectionContext";
import ModalContext from "../../../../contexts/ModalContext";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import { RouterOutletType } from "../index2";
import { GlobalTable, HeaderLine, Td, TdButtons, Th, Tr, LoadMoreButton } from "../../../../components/styles/table-style";
import { FeedbackContext } from "../../../../contexts/FeedbackContext";
import TableFiltersContext from "../../../../contexts/TableFiltersContext"; 

interface routerTableType {
    onChange?: () => void
}

const RouterTable: React.FC<routerTableType> = (props)=>{
    const dataFormatter = (isoDateString: string): string => {
        if (!isoDateString) {
            return ''; // Retorna vazio se a string for nula ou vazia
        }
    
        const dateUtc = new Date(isoDateString);
    
        // Verifica se a data é válida antes de tentar formatar
        if (isNaN(dateUtc.getTime())) {
            console.error("Data ISO inválida recebida:", isoDateString);
            return ''; // Trata o caso de uma string de data inválida
        }
    
        // Extrai as partes da data diretamente do UTC
        const year = dateUtc.getUTCFullYear();
        const month = dateUtc.getUTCMonth() + 1; // getUTCMonth é 0-indexado, então adiciona 1
        const day = dateUtc.getUTCDate();
    
        // Garante que mês e dia tenham dois dígitos (ex: 01 em vez de 1)
        const formattedMonth = month < 10 ? `0${month}` : month;
        const formattedDay = day < 10 ? `0${day}` : day;
    
        return `${formattedDay}/${formattedMonth}/${year}`;
    };

    const {router, setRouter} = useContext(RouterContext);
    const {setModalActive, setModalSection} = useContext(ModalContext); 
    const {setRouterSection} = useContext(SectionContext);
    const [routes, setRoutes] = useState<Router[]>([]); 
    const [page, setPage] = useState(0); 
    const [hasMore, setHasMore] = useState(true);
    
    const { setFeedback } = useContext(FeedbackContext);
    const { setTabIndex } = useOutletContext<RouterOutletType>();
    const navigate = useNavigate(); 

    const { routerStatusFilter } = useContext(TableFiltersContext);

    useEffect(() => {
        setTabIndex(1);
        setRouterSection('router');
    }, []);

    const handleEdit = async (id: number)=>{
        try {
            const fetchedRouter = await getRoute(id);
            setRouter(fetchedRouter);
            setRouterSection('newRouter'); 
            navigate('/panel/router/routers/edit');
        } catch (error) {
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar roteiro para edição!',
                statusOk: false
            });
        }
    };
    
    const handleView = async (id: number)=>{
        try {
            await getRoute(id); 
            navigate(`${id}`); 
        } catch (error) {
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar detalhes do roteiro!',
                statusOk: false
            });
        }
    };

    const deleteRoute = async (id: number)=>{
        try{
            await axios.delete(`${process.env.REACT_APP_ROUTER_BASE_URL}/route/${id}`);
            setPage(0); 
            setHasMore(true); 
            getRoutes(0, routerStatusFilter); 
            setFeedback({
                isOpen:true,
                message:'Rota deletada!',
                statusOk:true
              })
        }catch{
            setFeedback({
                isOpen:true,
                message:'Erro ao deletar rota!',
                statusOk:false
              })
        }
    }
    
    const getRoute = async (id: number): Promise<Router>=>{ 
        const response = await axios.get(`${process.env.REACT_APP_ROUTER_BASE_URL}/route/${id}`)
        const routeData = response.data as Router; 
        setRouter(routeData); 
        return routeData; 
    }

    const getRoutes = async (pageNumber: number, statusFilter: string | null = null) =>{
        try {
            let url = `${process.env.REACT_APP_ROUTER_BASE_URL}/route?page=${pageNumber}&size=7`;
            if (statusFilter && statusFilter !== '' && statusFilter !== 'Todos') {
                url += `&status=${encodeURIComponent(statusFilter)}`;
            }
            const response = await axios.get(url);
            const fetchedRoutes = response.data as Router[]; 
            return fetchedRoutes;
        } catch (error) {
            console.error("Erro ao buscar rotas:", error);
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar rotas!',
                statusOk: false
            });
            return []; 
        }
    };

    useEffect(() => {
        const fetchInitialRoutes = async () => {
            setPage(0);
            setHasMore(true);
            const initialRoutes = await getRoutes(0, routerStatusFilter);
            setRoutes(initialRoutes);
            if (initialRoutes.length < 7) {
                setHasMore(false);
            }
        };
        fetchInitialRoutes();
    }, [routerStatusFilter]); 

    const loadMoreRoutes = async () => {
        if (!hasMore) {
            return;
        }
        try {
            const nextPage = page + 1;
            const newRoutes = await getRoutes(nextPage, routerStatusFilter);
            if (newRoutes.length === 0 || newRoutes.length < 7) {
                setHasMore(false);
            }
            setRoutes(prevRoutes => [...prevRoutes, ...newRoutes]);
            setPage(nextPage);
        } catch (error) {
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar mais rotas',
                statusOk: false
            });
        }
    };

    const mapStatus = (status: string): string => {
        if (status === 'A programar') return 'pending';
        if (status === 'Rota programada') return 'pending'; 
        if (status === 'Rota cancelado') return 'cancel';
        if (status === 'Rota concluida' ) return 'completed';
        return '';
    };

    const filteredRoutes = useMemo(() => {
        return routes; 
    }, [routes]); 

    return(
        <>
            <GlobalTable>
                <thead>
                    <HeaderLine>
                        {headerRouterTable.map((header)=>(
                            <Th key={header}>{header}</Th>
                        ))}
                        <Th></Th>
                    </HeaderLine>
                </thead>
                <tbody>
                    {filteredRoutes.map((routerItem: Router)=>(
                        <Tr key={routerItem.id}>
                            <Td>{routerItem.id}</Td>
                            <Td>{routerItem.origin}</Td>
                            <Td> <StatusText className={mapStatus(routerItem.status)}>{routerItem.status}</StatusText> </Td>
                            <Td>{routerItem.vehicle?.plate}</Td> 
                            <Td>{dataFormatter(routerItem.date)}</Td>
                            <Td>{routerItem.track ? routerItem.track : "-"}</Td>
                            <TdButtons>
                                <OptionsImage onClick={async ()=> await handleView(routerItem.id!)} src={details} alt='Icon to show router'/>
                                <OptionsImage onClick={async ()=> await handleEdit(routerItem.id!)} src={edit} alt='Icon to edit router'/>
                                <OptionsImage onClick={async ()=> await deleteRoute(routerItem.id!)} src={trash} alt='Icon to delet router'/>
                            </TdButtons>
                        </Tr>
                    ))}
                </tbody>
            </GlobalTable>
            {hasMore && (
                <LoadMoreButton style={{marginBottom: '1rem'}} onClick={loadMoreRoutes}>
                    Carregar mais
                </LoadMoreButton>
            )}
        </>
    )
}

export default RouterTable;